/**
 * Display status PRESENTATION helpers per RFC-001 §6 + architect C2.
 *
 * RULE (binding): the FE does NOT derive `displayStatus`. BE is the canonical
 * source and ships `displayStatus` on every order/payment/pickup/claim payload
 * (phase1-fe-be-contract.md §3.1 / state-machine-spec §14).
 *
 * This module only maps a BE-supplied `displayStatus` to:
 *   - i18n key
 *   - badge variant (semantic, token-driven)
 *   - icon name (lucide identifier)
 *
 * If BE ships a value not in the known set, we surface a neutral UNKNOWN
 * sentinel and breadcrumb so the BE/FE contract drift is observable in Sentry
 * rather than silently rendering wrong UI.
 */

export type OrderDisplayStatus =
  | 'PAYMENT_PENDING'
  | 'PAYMENT_PROCESSING'
  | 'PAID'
  | 'PAYMENT_EXPIRED'
  | 'PAYMENT_FAILED'
  | 'PROCESSING'
  | 'READY_TO_SHIP'
  | 'SHIPPED'
  | 'DELIVERED'
  | 'FULFILLMENT_FAILED'
  | 'PICKUP_WAITING_PREP'
  | 'PICKUP_READY'
  | 'PICKUP_IN_PROGRESS'
  | 'PICKUP_COMPLETED'
  | 'PICKUP_NO_SHOW'
  | 'PICKUP_CANCELED'
  | 'CLAIM_ACTIVE'
  | 'CLAIM_REQUESTED'
  | 'CLAIM_UNDER_REVIEW'
  | 'CLAIM_APPROVED'
  | 'CLAIM_REJECTED'
  | 'CLAIM_COLLECTING'
  | 'CLAIM_REFUND_PENDING'
  | 'CLAIM_COMPLETED'
  | 'CLAIM_FAILED'
  | 'CONFIRMED'
  | 'CANCELED';

/**
 * Vendor application FSM (Slice 7). 9 states per phase2-vendor-state-machine §3.
 * BE response (`status` + `displayStatus`) is canonical. FE uses `status`
 * (this enum) to pick badge variant + lucide icon, and surfaces `displayStatus`
 * (BE-supplied Korean label) as the visible text.
 */
export type VendorApplicationDisplayStatus =
  | 'DRAFT'
  | 'SUBMITTED'
  | 'UNDER_REVIEW'
  | 'APPROVED'
  | 'CREDENTIALS_ISSUED'
  | 'ACTIVE'
  | 'REJECTED'
  | 'RESUBMISSION_REQUESTED'
  | 'WITHDRAWN';

/**
 * Artist status (Slice 9). 2 states per ADR-028 §1.4. Soft archive only.
 * BE-derived; FE resolves badge + i18n key + lucide icon.
 */
export type ArtistDisplayStatus = 'ACTIVE' | 'ARCHIVED';

/**
 * Funding campaign FSM (Slice 11). 6 states per ADR-030 §1.3.
 */
export type FundingDisplayStatus =
  | 'DRAFT'
  | 'ACTIVE'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'CANCELLED'
  | 'SETTLED';

/**
 * Pledge FSM (Slice 11). 5 states per ADR-030 §1.4.
 */
export type PledgeDisplayStatus =
  | 'PENDING'
  | 'PAID'
  | 'CANCELLED'
  | 'REFUNDED'
  | 'SETTLED';

/**
 * Settlement Run FSM (Slice 14). 12 states per ADR-032 §1.3 / state-machine-spec §18.
 */
export type SettlementDisplayStatus =
  | 'COMPUTING'
  | 'COMPUTED'
  | 'PENDING_APPROVAL'
  | 'AGENT_APPROVED'
  | 'SUPER_APPROVED'
  | 'PAYOUT_INITIATED'
  | 'PAID_OUT'
  | 'PAYOUT_FAILED'
  | 'PAYOUT_RETRY'
  | 'MANUAL_INTERVENTION_REQUIRED'
  | 'CANCELED'
  | 'FAILED';

/**
 * Payout transaction status (Slice 14). 5 states per ERD §4.9.4.
 */
export type PayoutDisplayStatus =
  | 'PENDING'
  | 'INITIATED'
  | 'SUCCEEDED'
  | 'FAILED'
  | 'RETRY_SCHEDULED';

/**
 * Release schedule FSM (Slice 14). 4 states per ADR-032 §1.4.
 */
export type ReleaseDisplayStatus =
  | 'SCHEDULED'
  | 'PUBLISHED'
  | 'DELAYED'
  | 'CANCELLED';

export type BadgeVariant = 'neutral' | 'info' | 'progress' | 'success' | 'warn' | 'danger';

export interface DisplayStatusMeta {
  i18nKey: string;
  badge: BadgeVariant;
  icon: string; // lucide icon name
}

const MAP: Record<OrderDisplayStatus, DisplayStatusMeta> = {
  // 결제 대기(미결제·주문 유효기간 내). 결제 deriver 가 발행하나 BO MAP 갭으로 UNKNOWN 렌더되던 결함.
  PAYMENT_PENDING: { i18nKey: 'status.PAYMENT_PENDING', badge: 'info', icon: 'clock' },
  PAYMENT_PROCESSING: { i18nKey: 'status.PAYMENT_PROCESSING', badge: 'info', icon: 'clock' },
  PAID: { i18nKey: 'status.PAID', badge: 'success', icon: 'check-circle' },
  PAYMENT_EXPIRED: { i18nKey: 'status.PAYMENT_EXPIRED', badge: 'warn', icon: 'clock-alert' },
  PAYMENT_FAILED: { i18nKey: 'status.PAYMENT_FAILED', badge: 'danger', icon: 'x-circle' },
  PROCESSING: { i18nKey: 'status.PROCESSING', badge: 'progress', icon: 'loader' },
  READY_TO_SHIP: { i18nKey: 'status.READY_TO_SHIP', badge: 'info', icon: 'package' },
  SHIPPED: { i18nKey: 'status.SHIPPED', badge: 'progress', icon: 'truck' },
  DELIVERED: { i18nKey: 'status.DELIVERED', badge: 'success', icon: 'check' },
  FULFILLMENT_FAILED: { i18nKey: 'status.FULFILLMENT_FAILED', badge: 'danger', icon: 'alert-triangle' },
  PICKUP_WAITING_PREP: { i18nKey: 'status.PICKUP_WAITING_PREP', badge: 'info', icon: 'clock' },
  PICKUP_READY: { i18nKey: 'status.PICKUP_READY', badge: 'success', icon: 'package-check' },
  PICKUP_IN_PROGRESS: { i18nKey: 'status.PICKUP_IN_PROGRESS', badge: 'progress', icon: 'package-open' },
  PICKUP_COMPLETED: { i18nKey: 'status.PICKUP_COMPLETED', badge: 'success', icon: 'check' },
  PICKUP_NO_SHOW: { i18nKey: 'status.PICKUP_NO_SHOW', badge: 'warn', icon: 'user-x' },
  PICKUP_CANCELED: { i18nKey: 'status.PICKUP_CANCELED', badge: 'neutral', icon: 'x' },
  CLAIM_ACTIVE: { i18nKey: 'status.CLAIM_ACTIVE', badge: 'warn', icon: 'alert-circle' },
  CLAIM_REQUESTED: { i18nKey: 'status.CLAIM_REQUESTED', badge: 'info', icon: 'inbox' },
  CLAIM_UNDER_REVIEW: { i18nKey: 'status.CLAIM_UNDER_REVIEW', badge: 'progress', icon: 'search' },
  CLAIM_APPROVED: { i18nKey: 'status.CLAIM_APPROVED', badge: 'success', icon: 'check-circle' },
  CLAIM_REJECTED: { i18nKey: 'status.CLAIM_REJECTED', badge: 'danger', icon: 'x-circle' },
  CLAIM_COLLECTING: { i18nKey: 'status.CLAIM_COLLECTING', badge: 'progress', icon: 'package-x' },
  CLAIM_REFUND_PENDING: { i18nKey: 'status.CLAIM_REFUND_PENDING', badge: 'progress', icon: 'wallet' },
  CLAIM_COMPLETED: { i18nKey: 'status.CLAIM_COMPLETED', badge: 'success', icon: 'check' },
  CLAIM_FAILED: { i18nKey: 'status.CLAIM_FAILED', badge: 'danger', icon: 'alert-octagon' },
  CONFIRMED: { i18nKey: 'status.CONFIRMED', badge: 'success', icon: 'badge-check' },
  CANCELED: { i18nKey: 'status.CANCELED', badge: 'neutral', icon: 'circle-slash' },
};

const VENDOR_MAP: Record<VendorApplicationDisplayStatus, DisplayStatusMeta> = {
  DRAFT: { i18nKey: 'vendors.status.DRAFT', badge: 'neutral', icon: 'pencil-line' },
  SUBMITTED: { i18nKey: 'vendors.status.SUBMITTED', badge: 'info', icon: 'inbox' },
  UNDER_REVIEW: { i18nKey: 'vendors.status.UNDER_REVIEW', badge: 'progress', icon: 'search' },
  APPROVED: { i18nKey: 'vendors.status.APPROVED', badge: 'success', icon: 'check-circle' },
  CREDENTIALS_ISSUED: {
    i18nKey: 'vendors.status.CREDENTIALS_ISSUED',
    badge: 'success',
    icon: 'key-round',
  },
  ACTIVE: { i18nKey: 'vendors.status.ACTIVE', badge: 'success', icon: 'badge-check' },
  REJECTED: { i18nKey: 'vendors.status.REJECTED', badge: 'danger', icon: 'x-circle' },
  RESUBMISSION_REQUESTED: {
    i18nKey: 'vendors.status.RESUBMISSION_REQUESTED',
    badge: 'warn',
    icon: 'rotate-ccw',
  },
  WITHDRAWN: { i18nKey: 'vendors.status.WITHDRAWN', badge: 'neutral', icon: 'circle-slash' },
};

const ARTIST_MAP: Record<ArtistDisplayStatus, DisplayStatusMeta> = {
  ACTIVE: { i18nKey: 'artists.status.ACTIVE', badge: 'success', icon: 'badge-check' },
  ARCHIVED: { i18nKey: 'artists.status.ARCHIVED', badge: 'neutral', icon: 'archive' },
};

const FUNDING_MAP: Record<FundingDisplayStatus, DisplayStatusMeta> = {
  DRAFT: { i18nKey: 'funding.status.DRAFT', badge: 'neutral', icon: 'pencil-line' },
  ACTIVE: { i18nKey: 'funding.status.ACTIVE', badge: 'progress', icon: 'megaphone' },
  SUCCEEDED: { i18nKey: 'funding.status.SUCCEEDED', badge: 'success', icon: 'trophy' },
  FAILED: { i18nKey: 'funding.status.FAILED', badge: 'danger', icon: 'x-circle' },
  CANCELLED: { i18nKey: 'funding.status.CANCELLED', badge: 'neutral', icon: 'circle-slash' },
  SETTLED: { i18nKey: 'funding.status.SETTLED', badge: 'success', icon: 'banknote' },
};

const PLEDGE_MAP: Record<PledgeDisplayStatus, DisplayStatusMeta> = {
  PENDING: { i18nKey: 'funding.pledgeStatus.PENDING', badge: 'info', icon: 'clock' },
  PAID: { i18nKey: 'funding.pledgeStatus.PAID', badge: 'success', icon: 'check-circle' },
  CANCELLED: { i18nKey: 'funding.pledgeStatus.CANCELLED', badge: 'neutral', icon: 'circle-slash' },
  REFUNDED: { i18nKey: 'funding.pledgeStatus.REFUNDED', badge: 'warn', icon: 'wallet' },
  SETTLED: { i18nKey: 'funding.pledgeStatus.SETTLED', badge: 'success', icon: 'banknote' },
};

const SETTLEMENT_MAP: Record<SettlementDisplayStatus, DisplayStatusMeta> = {
  COMPUTING: { i18nKey: 'settlement.status.COMPUTING', badge: 'progress', icon: 'loader' },
  COMPUTED: { i18nKey: 'settlement.status.COMPUTED', badge: 'info', icon: 'calculator' },
  PENDING_APPROVAL: {
    i18nKey: 'settlement.status.PENDING_APPROVAL',
    badge: 'warn',
    icon: 'clock',
  },
  AGENT_APPROVED: {
    i18nKey: 'settlement.status.AGENT_APPROVED',
    badge: 'progress',
    icon: 'user-check',
  },
  SUPER_APPROVED: {
    i18nKey: 'settlement.status.SUPER_APPROVED',
    badge: 'success',
    icon: 'shield-check',
  },
  PAYOUT_INITIATED: {
    i18nKey: 'settlement.status.PAYOUT_INITIATED',
    badge: 'progress',
    icon: 'send',
  },
  PAID_OUT: { i18nKey: 'settlement.status.PAID_OUT', badge: 'success', icon: 'banknote' },
  PAYOUT_FAILED: {
    i18nKey: 'settlement.status.PAYOUT_FAILED',
    badge: 'danger',
    icon: 'x-circle',
  },
  PAYOUT_RETRY: {
    i18nKey: 'settlement.status.PAYOUT_RETRY',
    badge: 'warn',
    icon: 'rotate-ccw',
  },
  MANUAL_INTERVENTION_REQUIRED: {
    i18nKey: 'settlement.status.MANUAL_INTERVENTION_REQUIRED',
    badge: 'danger',
    icon: 'alert-triangle',
  },
  CANCELED: { i18nKey: 'settlement.status.CANCELED', badge: 'neutral', icon: 'circle-slash' },
  FAILED: { i18nKey: 'settlement.status.FAILED', badge: 'danger', icon: 'alert-octagon' },
};

const PAYOUT_MAP: Record<PayoutDisplayStatus, DisplayStatusMeta> = {
  PENDING: { i18nKey: 'settlement.payoutStatus.PENDING', badge: 'info', icon: 'clock' },
  INITIATED: {
    i18nKey: 'settlement.payoutStatus.INITIATED',
    badge: 'progress',
    icon: 'send',
  },
  SUCCEEDED: {
    i18nKey: 'settlement.payoutStatus.SUCCEEDED',
    badge: 'success',
    icon: 'check-circle',
  },
  FAILED: { i18nKey: 'settlement.payoutStatus.FAILED', badge: 'danger', icon: 'x-circle' },
  RETRY_SCHEDULED: {
    i18nKey: 'settlement.payoutStatus.RETRY_SCHEDULED',
    badge: 'warn',
    icon: 'rotate-ccw',
  },
};

const RELEASE_MAP: Record<ReleaseDisplayStatus, DisplayStatusMeta> = {
  SCHEDULED: {
    i18nKey: 'settlement.releaseStatus.SCHEDULED',
    badge: 'info',
    icon: 'calendar-clock',
  },
  PUBLISHED: {
    i18nKey: 'settlement.releaseStatus.PUBLISHED',
    badge: 'success',
    icon: 'megaphone',
  },
  DELAYED: {
    i18nKey: 'settlement.releaseStatus.DELAYED',
    badge: 'warn',
    icon: 'clock-alert',
  },
  CANCELLED: {
    i18nKey: 'settlement.releaseStatus.CANCELLED',
    badge: 'neutral',
    icon: 'circle-slash',
  },
};

const UNKNOWN_META: DisplayStatusMeta = {
  i18nKey: 'status.UNKNOWN',
  badge: 'neutral',
  icon: 'help-circle',
};

export interface BreadcrumbReporter {
  (event: { category: string; message: string; data?: Record<string, unknown> }): void;
}

let reporter: BreadcrumbReporter | null = null;

export const setDisplayStatusBreadcrumbReporter = (r: BreadcrumbReporter | null): void => {
  reporter = r;
};

/**
 * Look up presentation metadata for a BE-supplied displayStatus string.
 * Unknown values return UNKNOWN_META and emit a Sentry breadcrumb (architect C2).
 */
export const resolveDisplayStatus = (
  status: string | null | undefined,
  ctx?: { orderId?: string; entity?: string },
): DisplayStatusMeta => {
  if (status && status in MAP) {
    return MAP[status as OrderDisplayStatus];
  }
  reporter?.({
    category: 'display-status',
    message: `unknown displayStatus: ${status ?? '<missing>'}`,
    data: { status, ...ctx },
  });
  return UNKNOWN_META;
};

export const assertDisplayStatusPresent = <T extends { displayStatus?: string | null }>(
  payload: T,
  entity: string,
): void => {
  if (!payload.displayStatus) {
    reporter?.({
      category: 'display-status',
      message: `BE response missing displayStatus on ${entity}`,
      data: { entity },
    });
  }
};

/**
 * Vendor application FSM 9 → presentation meta. BE ships `status` (enum) +
 * `displayStatus` (Korean label). FE uses `status` for variant/icon, label
 * from BE. Unknown FSM value → UNKNOWN_META + breadcrumb.
 */
export const resolveVendorApplicationStatus = (
  status: string | null | undefined,
  ctx?: { applicationId?: string },
): DisplayStatusMeta => {
  if (status && status in VENDOR_MAP) {
    return VENDOR_MAP[status as VendorApplicationDisplayStatus];
  }
  reporter?.({
    category: 'display-status',
    message: `unknown vendor application status: ${status ?? '<missing>'}`,
    data: { status, entity: 'vendorApplication', ...ctx },
  });
  return UNKNOWN_META;
};

/**
 * Artist status (Slice 9) → presentation meta. ACTIVE / ARCHIVED only.
 */
export const resolveArtistStatus = (
  status: string | null | undefined,
  ctx?: { artistId?: string },
): DisplayStatusMeta => {
  if (status && status in ARTIST_MAP) {
    return ARTIST_MAP[status as ArtistDisplayStatus];
  }
  reporter?.({
    category: 'display-status',
    message: `unknown artist status: ${status ?? '<missing>'}`,
    data: { status, entity: 'artist', ...ctx },
  });
  return UNKNOWN_META;
};

/**
 * Funding campaign status (Slice 11) → presentation meta. 6 FSM states.
 */
export const resolveFundingStatus = (
  status: string | null | undefined,
  ctx?: { campaignId?: string },
): DisplayStatusMeta => {
  if (status && status in FUNDING_MAP) {
    return FUNDING_MAP[status as FundingDisplayStatus];
  }
  reporter?.({
    category: 'display-status',
    message: `unknown funding status: ${status ?? '<missing>'}`,
    data: { status, entity: 'funding', ...ctx },
  });
  return UNKNOWN_META;
};

/**
 * Pledge status (Slice 11) → presentation meta. 5 FSM states.
 */
export const resolvePledgeStatus = (
  status: string | null | undefined,
  ctx?: { pledgeId?: string },
): DisplayStatusMeta => {
  if (status && status in PLEDGE_MAP) {
    return PLEDGE_MAP[status as PledgeDisplayStatus];
  }
  reporter?.({
    category: 'display-status',
    message: `unknown pledge status: ${status ?? '<missing>'}`,
    data: { status, entity: 'pledge', ...ctx },
  });
  return UNKNOWN_META;
};

/**
 * Settlement Run status (Slice 14) → presentation meta. 12 FSM states.
 */
export const resolveSettlementStatus = (
  status: string | null | undefined,
  ctx?: { runId?: string },
): DisplayStatusMeta => {
  if (status && status in SETTLEMENT_MAP) {
    return SETTLEMENT_MAP[status as SettlementDisplayStatus];
  }
  reporter?.({
    category: 'display-status',
    message: `unknown settlement status: ${status ?? '<missing>'}`,
    data: { status, entity: 'settlement', ...ctx },
  });
  return UNKNOWN_META;
};

/**
 * Payout transaction status (Slice 14) → presentation meta.
 */
export const resolvePayoutStatus = (
  status: string | null | undefined,
  ctx?: { payoutTxId?: string },
): DisplayStatusMeta => {
  if (status && status in PAYOUT_MAP) {
    return PAYOUT_MAP[status as PayoutDisplayStatus];
  }
  reporter?.({
    category: 'display-status',
    message: `unknown payout status: ${status ?? '<missing>'}`,
    data: { status, entity: 'payout', ...ctx },
  });
  return UNKNOWN_META;
};

/**
 * Release schedule status (Slice 14) → presentation meta. 4 FSM states.
 */
export const resolveReleaseStatus = (
  status: string | null | undefined,
  ctx?: { scheduleId?: string },
): DisplayStatusMeta => {
  if (status && status in RELEASE_MAP) {
    return RELEASE_MAP[status as ReleaseDisplayStatus];
  }
  reporter?.({
    category: 'display-status',
    message: `unknown release schedule status: ${status ?? '<missing>'}`,
    data: { status, entity: 'release', ...ctx },
  });
  return UNKNOWN_META;
};
